import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

const RECENTS_KEY = "qsp_recent_tickers";

function loadRecents() {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
  } catch {
    return [];
  }
}

export function pushRecent(row) {
  if (!row || !row.ticker) return;
  const prev = loadRecents().filter((r) => r.ticker !== row.ticker);
  const next = [row, ...prev].slice(0, 8);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
}

export default function TickerSearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setRecents(loadRecents());
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/market/search", {
          params: { q: query, limit: 10 },
        });
        setResults(data.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [q, open]);

  const goTo = (row) => {
    pushRecent({
      ticker: row.ticker,
      name: row.name,
      exchange: row.exchange,
    });
    onOpenChange(false);
    navigate(`/stock/${encodeURIComponent(row.ticker)}`);
  };

  const items = useMemo(() => results || [], [results]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="sr-only">Search US stock ticker</DialogTitle>
      <div className="bg-[#0B111C] border-b border-[#1E293B]">
        <CommandInput
          data-testid="ticker-search-input"
          value={q}
          onValueChange={setQ}
          placeholder="Search US stock ticker (NVDA, AAPL, SPY)…"
          className="mono text-sm text-slate-100 placeholder:text-slate-600"
        />
      </div>
      <CommandList className="bg-[#0B111C] max-h-[420px]">
        {loading && (
          <div className="py-6 flex items-center justify-center gap-2 text-slate-500 mono text-xs">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching Alpaca…
          </div>
        )}
        {!loading && !q.trim() && recents.length > 0 && (
          <CommandGroup heading="RECENT">
            {recents.map((r) => (
              <CommandItem
                key={`recent-${r.ticker}`}
                data-testid={`ticker-recent-${r.ticker}`}
                value={`recent-${r.ticker}-${r.name || ""}`}
                onSelect={() => goTo(r)}
                className="text-slate-200"
              >
                <div className="flex-1 flex items-center gap-3">
                  <span className="mono font-bold text-slate-100 w-16">{r.ticker}</span>
                  <span className="text-slate-400 text-xs truncate">{r.name || "—"}</span>
                </div>
                <span className="mono text-[10px] text-slate-500 uppercase">
                  {r.exchange || ""}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!loading && q.trim() && items.length === 0 && (
          <CommandEmpty className="py-8 text-center mono text-xs text-slate-500">
            No tickers match "{q}"
          </CommandEmpty>
        )}
        {!loading && items.length > 0 && (
          <CommandGroup heading="RESULTS">
            {items.map((r) => (
              <CommandItem
                key={r.ticker}
                data-testid={`ticker-search-option-${r.ticker}`}
                value={`${r.ticker} ${r.name || ""}`}
                onSelect={() => goTo(r)}
                className="text-slate-200"
              >
                <div className="flex-1 flex items-center gap-3 min-w-0">
                  <span className="mono font-bold text-slate-100 w-16 shrink-0">
                    {r.ticker}
                  </span>
                  <span className="text-slate-400 text-xs truncate">{r.name}</span>
                </div>
                <span className="mono text-[10px] text-slate-500 uppercase shrink-0 ml-2">
                  {r.exchange}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
      <div className="border-t border-[#1E293B] px-3 py-2 flex items-center justify-between bg-[#080B11]">
        <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
          US Equities · Alpaca · Real-time SIP
        </span>
        <span className="mono text-[10px] text-slate-500">
          <kbd className="px-1 py-0.5 border border-slate-700 mr-1">↵</kbd>select
          <kbd className="px-1 py-0.5 border border-slate-700 mx-1">esc</kbd>close
        </span>
      </div>
    </CommandDialog>
  );
}
